import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, faceQuad, faces, frontFace, iso } from './iso.js';
import type { FurniDrawer } from './iso.js';

/** Drawers for the sports and gym furniture set (see protocol furniture/sports.ts). */

/** Whether an item runs along the tile's u axis (rotations 0 and 4) or its v axis (2 and 6). */
const alongU = (rotation: number) => rotation === 0 || rotation === 4;

function drawBadmintonNet(pc: PixelCanvas, rotation: number): void {
  const along = alongU(rotation);
  const post = faces('#3a3a42', '#5a5a62');
  const posts = along
    ? [box(-0.45, -0.37, -0.04, 0.04, 0, 34, post), box(0.37, 0.45, -0.04, 0.04, 0, 34, post)]
    : [box(-0.04, 0.04, -0.45, -0.37, 0, 34, post), box(-0.04, 0.04, 0.37, 0.45, 0, 34, post)];
  drawBoxes(pc, posts);

  // Net mesh strung between the posts.
  for (let s = -0.37; s <= 0.37; s += 0.08) {
    const top = along ? iso(s, 0, 34) : iso(0, s, 34);
    const bot = along ? iso(s, 0, 4) : iso(0, s, 4);
    pc.line(top.x, top.y, bot.x, bot.y, '#ffffff66');
  }
  const tapeA = along ? iso(-0.45, 0, 34) : iso(0, -0.45, 34);
  const tapeB = along ? iso(0.45, 0, 34) : iso(0, 0.45, 34);
  pc.line(tapeA.x, tapeA.y, tapeB.x, tapeB.y, '#ffffff');
  pc.line(tapeA.x, tapeA.y - 1, tapeB.x, tapeB.y - 1, '#ffffff');
}

function drawSoccerGoal(pc: PixelCanvas, rotation: number): void {
  const wide = alongU(rotation);
  const white = faces('#f1eee6', '#ffffff');
  const posts = wide
    ? box(-0.42, 0.42, -0.03, 0.03, 0, 36, white)
    : box(-0.03, 0.03, -0.42, 0.42, 0, 36, white);
  const crossbar = wide
    ? box(-0.42, 0.42, -0.03, 0.03, 32, 36, white)
    : box(-0.03, 0.03, -0.42, 0.42, 32, 36, white);
  drawBoxes(pc, [posts, crossbar]);

  const face = frontFace(rotation) ?? 'left';
  const at = 0.03;
  for (let s = -0.36; s <= 0.34; s += 0.09) faceQuad(pc, face, at, s, s + 0.015, 0, 32, '#ffffff66');
  for (let h = 4; h <= 28; h += 8) faceQuad(pc, face, at, -0.4, 0.4, h, h + 0.015, '#ffffff66');
}

function drawBasketballHoop(pc: PixelCanvas, rotation: number): void {
  const pole = faces('#4a4a52', '#5a5a62');
  drawBox(pc, box(-0.04, 0.04, -0.04, 0.04, 0, 64, pole));

  const face = frontFace(rotation) ?? 'left';
  const at = 0.04;
  faceQuad(pc, face, at, -0.2, 0.2, 58, 80, '#e8e4da');
  faceQuad(pc, face, at, -0.13, 0.13, 62, 72, '#2f2f38');

  const rim = face === 'left' ? iso(0, at + 0.05, 60) : iso(at + 0.05, 0, 60);
  pc.ellipse(rim.x, rim.y, 6, 2, '#e8702c');
  pc.line(rim.x - 5, rim.y, rim.x, rim.y + 9, '#ffffff99');
  pc.line(rim.x + 5, rim.y, rim.x, rim.y + 9, '#ffffff99');
  pc.line(rim.x, rim.y, rim.x, rim.y + 9, '#ffffff99');
}

function drawGymTreadmill(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.4, 0.4, -0.22, 0.22, 0, 10, faces('#2a2a33', '#34343c')));
  for (let u = -0.34; u <= 0.34; u += 0.08) {
    const p = iso(u, 0, 10);
    pc.rect(p.x - 1, p.y - 3, 2, 3, '#3a3a44');
  }
  drawBox(pc, box(0.3, 0.4, -0.22, 0.22, 10, 48, faces('#8a8f94', '#9ba0a6')));

  const face = frontFace(rotation) ?? 'left';
  const at = 0.4;
  faceQuad(pc, face, at, -0.16, 0.16, 30, 42, '#1a1a20');
  faceQuad(pc, face, at, -0.1, 0.1, 33, 39, '#5ad1ff');
}

function drawDumbbellRack(pc: PixelCanvas, rotation: number): void {
  const metal = faces('#4a4a52', '#5a5a62');
  const wide = alongU(rotation);
  const piece = wide
    ? box(-0.42, 0.42, -0.14, 0.14, 0, 50, metal)
    : box(-0.14, 0.14, -0.42, 0.42, 0, 50, metal);
  drawBox(pc, piece);

  const face = frontFace(rotation);
  if (!face) return;
  const at = 0.14;
  const sizes = [3, 4, 5, 6, 5, 4, 3];
  for (const base of [8, 26, 42]) {
    faceQuad(pc, face, at, -0.4, 0.4, base - 2, base, '#2f2f38');
    let s = -0.36;
    for (const sz of sizes) {
      faceQuad(pc, face, at, s, s + 0.08, base, base + sz, '#1a1a20');
      s += 0.09;
    }
  }
}

function drawYogaMat(pc: PixelCanvas, rotation: number): void {
  const wide = alongU(rotation);
  const [u0, u1, v0, v1] = wide ? [-0.42, 0.42, -0.18, 0.18] : [-0.18, 0.18, -0.42, 0.42];
  pc.poly([iso(u0!, v0!, 1), iso(u1!, v0!, 1), iso(u1!, v1!, 1), iso(u0!, v1!, 1)], '#7fb7a0');
  pc.poly([iso(u0!, v0!, 0), iso(u1!, v0!, 0), iso(u1!, v1!, 0), iso(u0!, v1!, 0)], '#5c9c86');
  const a = wide ? iso(u0!, 0, 1) : iso(0, v0!, 1);
  const b = wide ? iso(u1!, 0, 1) : iso(0, v1!, 1);
  pc.line(a.x, a.y, b.x, b.y, '#ffffff55');
}

function drawPingPongTable(pc: PixelCanvas, rotation: number): void {
  const wide = alongU(rotation);
  const top = faces('#1f6f4f', '#2f9e6c');
  const tabletop = wide
    ? box(-0.44, 0.44, -0.2, 0.2, 24, 28, top)
    : box(-0.2, 0.2, -0.44, 0.44, 24, 28, top);
  const legColor = faces('#2a2a33');
  const legs = wide
    ? [
        box(-0.4, -0.34, -0.16, -0.1, 0, 24, legColor),
        box(0.34, 0.4, -0.16, -0.1, 0, 24, legColor),
        box(-0.4, -0.34, 0.1, 0.16, 0, 24, legColor),
        box(0.34, 0.4, 0.1, 0.16, 0, 24, legColor),
      ]
    : [
        box(-0.16, -0.1, -0.4, -0.34, 0, 24, legColor),
        box(-0.16, -0.1, 0.34, 0.4, 0, 24, legColor),
        box(0.1, 0.16, -0.4, -0.34, 0, 24, legColor),
        box(0.1, 0.16, 0.34, 0.4, 0, 24, legColor),
      ];
  drawBoxes(pc, [tabletop, ...legs]);

  // The net always runs perpendicular to the table's long axis, whichever way it's rotated.
  const netTop0 = wide ? iso(0, -0.2, 36) : iso(-0.2, 0, 36);
  const netTop1 = wide ? iso(0, 0.2, 36) : iso(0.2, 0, 36);
  const netBot0 = wide ? iso(0, -0.2, 28) : iso(-0.2, 0, 28);
  const netBot1 = wide ? iso(0, 0.2, 28) : iso(0.2, 0, 28);
  pc.poly([netTop0, netTop1, netBot1, netBot0], '#ffffffaa');
  pc.line(netTop0.x, netTop0.y, netTop1.x, netTop1.y, '#ffffff');
  pc.line(netBot0.x, netBot0.y, netBot1.x, netBot1.y, '#ffffff');
}

function drawGymLocker(pc: PixelCanvas, rotation: number): void {
  const metal = faces('#5a7a8f', '#7a9ab0');
  drawBox(pc, box(-0.4, 0.4, -0.3, 0.3, 0, 56, metal));

  const face = frontFace(rotation) ?? 'left';
  const at = 0.3;
  faceQuad(pc, face, at, -0.36, 0.36, 2, 54, '#3a5a6e');
  faceQuad(pc, face, at, -0.36, -0.02, 4, 52, '#4a7085');
  faceQuad(pc, face, at, 0.02, 0.36, 4, 52, '#4a7085');

  const handle1 = face === 'left' ? iso(-0.04, at, 30) : iso(at, -0.04, 30);
  const handle2 = face === 'left' ? iso(0.04, at, 30) : iso(at, 0.04, 30);
  pc.rect(handle1.x, handle1.y, 1, 4, '#f0bf36');
  pc.rect(handle2.x, handle2.y, 1, 4, '#f0bf36');
}

export const SPORTS_DRAWERS: Record<string, FurniDrawer> = {
  badminton_net: drawBadmintonNet,
  soccer_goal: drawSoccerGoal,
  basketball_hoop: drawBasketballHoop,
  gym_treadmill: drawGymTreadmill,
  dumbbell_rack: drawDumbbellRack,
  yoga_mat: drawYogaMat,
  ping_pong_table: drawPingPongTable,
  gym_locker: drawGymLocker,
};
